import { motion } from 'framer-motion';
import {
  CSSProperties,
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useToggle } from '~/hooks';
import { Box } from '../Box';
import './SmartTooltip.scss';

export type TTooltipPosition = 'top' | 'bottom' | 'left' | 'right';
export type TTooltipAlignment = 'start' | 'center' | 'end';
export type TTooltipTrigger = 'hover' | 'click';

interface ISmartTooltipProps {
  children: ReactNode;
  content: ReactNode;
  alignment?: TTooltipAlignment;
  className?: string;
  delay?: number;
  disabled?: boolean;
  hideArrow?: boolean;
  position?: TTooltipPosition;
  style?: Partial<{
    Content: CSSProperties;
    Portal: CSSProperties;
    Root: CSSProperties;
  }>;
  trigger?: TTooltipTrigger;
}

const tooltipVariants = {
  show: { opacity: 1, display: 'flex' },
  hide: { opacity: 0, transitionEnd: { display: 'none' } },
};

export const SmartTooltip: FunctionComponent<ISmartTooltipProps> = ({
  alignment = 'center',
  children,
  className = '',
  content,
  delay = 0,
  disabled = false,
  hideArrow = false,
  position = 'top',
  style,
  trigger = 'hover',
}) => {
  const [isOpen, controls] = useToggle(false);
  const [actualPosition, setActualPosition] = useState<TTooltipPosition>(position);
  const [actualAlignment, setActualAlignment] = useState<TTooltipAlignment>(alignment);
  const [tooltipCoords, setTooltipCoords] = useState({ top: 0, left: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const calculateOptimalPosition = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let optimalPosition = position;
    let optimalAlignment = alignment;

    // Check if tooltip fits in the preferred position
    const spaceTop = container.top;
    const spaceBottom = viewport.height - container.bottom;
    const spaceLeft = container.left;
    const spaceRight = viewport.width - container.right;

    // Use estimated dimensions since tooltip might not be visible yet
    const estimatedTooltipHeight = 80; // Reasonable estimate for typical tooltip
    const estimatedTooltipWidth = 250; // Max width from CSS
    const offset = 6; // Tooltip offset from trigger

    // Position fallback logic
    if (position === 'top' && spaceTop < estimatedTooltipHeight) {
      optimalPosition =
        spaceBottom >= estimatedTooltipHeight
          ? 'bottom'
          : spaceRight >= estimatedTooltipWidth
            ? 'right'
            : 'left';
    } else if (position === 'bottom' && spaceBottom < estimatedTooltipHeight) {
      optimalPosition =
        spaceTop >= estimatedTooltipHeight
          ? 'top'
          : spaceRight >= estimatedTooltipWidth
            ? 'right'
            : 'left';
    } else if (position === 'left' && spaceLeft < estimatedTooltipWidth) {
      optimalPosition =
        spaceRight >= estimatedTooltipWidth
          ? 'right'
          : spaceTop >= estimatedTooltipHeight
            ? 'top'
            : 'bottom';
    } else if (position === 'right' && spaceRight < estimatedTooltipWidth) {
      optimalPosition =
        spaceLeft >= estimatedTooltipWidth
          ? 'left'
          : spaceTop >= estimatedTooltipHeight
            ? 'top'
            : 'bottom';
    }

    // Alignment fallback logic for horizontal positions
    if (optimalPosition === 'top' || optimalPosition === 'bottom') {
      if (alignment === 'start' && container.left < estimatedTooltipWidth / 2) {
        optimalAlignment = 'center';
      } else if (
        alignment === 'end' &&
        container.right + estimatedTooltipWidth / 2 > viewport.width
      ) {
        optimalAlignment = 'center';
      }
    }

    // Alignment fallback logic for vertical positions
    if (optimalPosition === 'left' || optimalPosition === 'right') {
      if (alignment === 'start' && container.top < estimatedTooltipHeight / 2) {
        optimalAlignment = 'center';
      } else if (
        alignment === 'end' &&
        container.bottom + estimatedTooltipHeight / 2 > viewport.height
      ) {
        optimalAlignment = 'center';
      }
    }

    // Calculate absolute coordinates for portal rendering
    let top = 0;
    let left = 0;

    // Calculate position based on optimal position and alignment
    if (optimalPosition === 'top') {
      top = container.top - offset;
      if (optimalAlignment === 'start') {
        left = container.left;
      } else if (optimalAlignment === 'end') {
        left = container.right;
      } else {
        left = container.left + container.width / 2;
      }
    } else if (optimalPosition === 'bottom') {
      top = container.bottom + offset;
      if (optimalAlignment === 'start') {
        left = container.left;
      } else if (optimalAlignment === 'end') {
        left = container.right;
      } else {
        left = container.left + container.width / 2;
      }
    } else if (optimalPosition === 'left') {
      left = container.left - offset;
      if (optimalAlignment === 'start') {
        top = container.top;
      } else if (optimalAlignment === 'end') {
        top = container.bottom;
      } else {
        top = container.top + container.height / 2;
      }
    } else if (optimalPosition === 'right') {
      left = container.right + offset;
      if (optimalAlignment === 'start') {
        top = container.top;
      } else if (optimalAlignment === 'end') {
        top = container.bottom;
      } else {
        top = container.top + container.height / 2;
      }
    }

    setActualPosition(optimalPosition);
    setActualAlignment(optimalAlignment);
    setTooltipCoords({ top, left });
  }, [position, alignment]);

  const handleShow = useCallback(() => {
    if (disabled) return;

    // Calculate position before showing to prevent anchor shift
    calculateOptimalPosition();

    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        controls.setOn();
      }, delay);
    } else {
      controls.setOn();
    }
  }, [controls, delay, disabled, calculateOptimalPosition]);

  const handleHide = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    controls.setOff();
  }, [controls]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Update tooltip position on scroll and resize
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      calculateOptimalPosition();
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, calculateOptimalPosition]);

  // Helper function to get transform based on position and alignment
  const getTransform = (): string => {
    const transforms: string[] = [];

    // Handle position-based transforms
    if (actualPosition === 'top') {
      transforms.push('translateY(-100%)');
    }

    // Handle alignment-based transforms
    if ((actualPosition === 'top' || actualPosition === 'bottom') && actualAlignment === 'center') {
      transforms.push('translateX(-50%)');
    } else if (
      (actualPosition === 'top' || actualPosition === 'bottom') &&
      actualAlignment === 'end'
    ) {
      transforms.push('translateX(-100%)');
    }

    if ((actualPosition === 'left' || actualPosition === 'right') && actualAlignment === 'center') {
      transforms.push('translateY(-50%)');
    } else if (
      (actualPosition === 'left' || actualPosition === 'right') &&
      actualAlignment === 'end'
    ) {
      transforms.push('translateY(-100%)');
    }

    if (actualPosition === 'left') {
      transforms.push('translateX(-100%)');
    }

    return transforms.join(' ') || 'none';
  };

  const triggerProps =
    trigger === 'hover'
      ? { onMouseEnter: handleShow, onMouseLeave: handleHide, onClick: handleHide }
      : { onClick: isOpen ? handleHide : handleShow };

  if (disabled) {
    return <>{children}</>;
  }

  // Render tooltip via Portal
  const tooltipClassName = [
    'smart-tooltip-portal',
    `smart-tooltip-portal--${actualPosition}-${actualAlignment}`,
    hideArrow && 'smart-tooltip-portal--no-arrow',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const tooltipPortal = createPortal(
    <motion.div
      ref={tooltipRef}
      className={tooltipClassName}
      initial="hide"
      animate={isOpen ? 'show' : 'hide'}
      transition={{ duration: 0.3 }}
      variants={tooltipVariants}
      style={{
        position: 'fixed',
        top: `${tooltipCoords.top}px`,
        left: `${tooltipCoords.left}px`,
        transform: getTransform(),
        ...style?.Portal,
      }}
    >
      <article className="smart-tooltip-content" style={style?.Content}>
        {content}
      </article>
    </motion.div>,
    document.body,
  );

  return (
    <>
      <Box className="smart-tooltip-wrapper" style={style?.Root} ref={containerRef}>
        <Box className="smart-tooltip-trigger" {...triggerProps}>
          {children}
        </Box>
      </Box>
      {tooltipPortal}
    </>
  );
};
